/* eslint-disable @typescript-eslint/camelcase */
import debounce from 'lodash/debounce';
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { scroller } from '../../js/app/react/helpers.objects';
import AdvForm from '../CommonComponents/AdvForm';
import MissingInfo from '../ErrorPages/MissingInfoView';
import PlanNotStarted from '../CommonComponents/PlanNotStarted';
import TableVerticalSide from '../ProjectedTableVerticalSide/TableVerticalSideView';
import {
  getRowClassProperty,
  getTableClassProperty,
  getTableSideProps,
} from './ProjectedBalanceSheetHelpers';
import { Button } from '../CommonComponents/OPL/Button/Button';
import { PeriodsPropTypes, ProjectedItemsPropTypes } from './ProjectedBalanceSheetPropTypes';
import { ProjectedBalanceSheetBreakdown } from './components/ProjectedBalanceSheetBreakdown';
import { PDFTitle } from '../PDFRenderer/components/PDFTitle/PDFTitle';
import { RawButton } from '../CommonComponents/OPL/Button/Button.styles';
import { Icon } from '../CommonComponents/OPL/Icon/Icon';
import { Box } from '../CommonComponents/OPL/Box/Box';
import { Text } from '../CommonComponents/OPL/Text/Text';
import { Select } from '../CommonComponents/OPL/Select/Select/Select';
import { urlWithParams } from '../CommonUtils/urlWithParams';

class ProjectedBalanceSheetView extends Component {
  constructor(props) {
    super(props);

    this.state = {
      breakdown: {
        earnings: null,
        contributions: null,
        distributions: null,
        payment: null,
        interest: null,
        investmentIncomeTax: null,
        capitalGainsTax: null,
        costBasis: null,
        exerciseCost: null,
        cashInterestTax: null,
      },
      expanded_rows: [],
      stopLeftScroll: true,
      stopRightScroll: false,
      isScrolling: false,
    };

    this.render_expanded = this.render_expanded.bind(this);
    this.scrollToYear = this.scrollToYear.bind(this);
  }

  componentDidMount() {
    const { is_current, fpId, clientId, portalName } = this.props;

    document.title = `Reports - Balance Sheet | ${portalName}`;

    this.fetch(is_current, fpId, clientId);
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    const { fpId } = this.props;
    const { clientId, fpId: nextFpId, is_current } = nextProps;

    if (nextFpId !== fpId) {
      this.fetch(is_current, nextFpId, clientId);
    }
  }

  componentWillUnmount() {
    const { is_projected } = this.props;
    if (is_projected) {
      this.setProjected();
    }
  }

  setExpanded(owner, group_name, self) {
    const { expanded_rows } = self.state;
    const local_rows = expanded_rows.slice();
    const row_index = expanded_rows.indexOf(`${owner}|${group_name}`);

    if (row_index > -1) {
      local_rows.splice(row_index, 1);
    } else {
      local_rows.push(`${owner}|${group_name}`);
    }

    this.setState({
      expanded_rows: local_rows,
    });
  }

  setProjected() {
    const { is_projected, set_projected, clientId } = this.props;

    set_projected(!is_projected, clientId);
  }

  setScrollAvailability() {
    const setAvailability = () => {
      const scrollOffset = this.scrollContainer.scrollLeft;
      this.setState({
        stopLeftScroll: scrollOffset <= 0,
        stopRightScroll:
          scrollOffset >= this.scrollContainer.scrollWidth - this.scrollContainer.offsetWidth,
      });
    };
    debounce(setAvailability, 150)();
  }

  fetch(is_current, fpId, clientId, expandCurrentYear) {
    const { fetch_data, isPrint } = this.props;

    fetch_data(is_current, null, clientId, fpId, isPrint, expandCurrentYear);
  }

  show_breakdown(e, breakdown) {
    const { target } = e;
    const bounding_rect = target.getBoundingClientRect();

    if (Object.values(breakdown).some(value => !!value)) {
      target.classList.add('highlight');
    }

    this.setState({
      breakdown,
      show_breakdown: true,
      breakdown_top: bounding_rect.top,
      breakdown_left: bounding_rect.left,
      breakdown_width: bounding_rect.width,
      breakdown_height: bounding_rect.height,
    });
  }

  scrollToYear(opt) {
    this.scrollContainer.scrollLeft = 0;
    const val = opt.value;
    const yearTarget = document.getElementById(`year_${val}`);
    this.scrollContainer.scrollLeft =
      ADV.util.getPosition(yearTarget).left - ADV.util.getPosition(this.scrollContainer).left;
  }

  hide_breakdown(e) {
    const { target } = e;

    target.classList.remove('highlight');

    this.setState({
      show_breakdown: false,
      earnings: null,
      contributions: null,
      distributions: null,
      payment: null,
      interest: null,
    });
  }

  render_expanded(expanded) {
    const { isPrint } = this.props;

    if (isPrint) {
      return null;
    }
    return <i className={`fa ${expanded ? 'fa-minus' : 'fa-plus'} expando`} />;
  }

  render() {
    const {
      is_initialized,
      ProjectedItems,
      Periods,
      PrincipalAgeOptions,
      SpouseAgeOptions,
      isPrint,
      clientId,
      error_type,
      errors,
      Milestones,
      title,
      is_current,
      fpId,
    } = this.props;

    const {
      breakdown,
      expanded_rows,
      stopRightScroll,
      stopLeftScroll,
      show_breakdown,
      breakdown_top,
      breakdown_left,
      breakdown_width,
      isScrolling,
    } = this.state;

    let asset_groups = [];
    let liability_groups = [];

    switch (error_type) {
      case 'PlanNotStarted':
        return <PlanNotStarted clientId={clientId} />;
      case 'MissingInfo':
        return <MissingInfo clientId={clientId} errors={errors} />;
      default:
    }

    if (is_initialized) {
      if (!ProjectedItems || !Periods) {
        return null;
      }

      asset_groups = ProjectedItems[0].Assets.AssetGroups.slice();
      liability_groups = ProjectedItems[0].Liabilities.LiabilityGroups.slice();

      const assetsSubheaderOffset = 0;
      const assetsGroupsOffset = assetsSubheaderOffset + 1;
      const liabilitiesSubheaderOffset = assetsGroupsOffset + asset_groups.length;
      const liabilitiesGroupsOffset = liabilitiesSubheaderOffset + 1;
      const netWorthOffset = 0;

      const localPeriods = isPrint ? Periods.slice(0, 5) : Periods;

      const setExpanded = (owner, groupName) => () => this.setExpanded(owner, groupName, this);

      const widget = (
        <section
          className="owner-table"
          onScroll={() => this.setScrollAvailability()}
          ref={c => {
            this.scrollContainer = c;
          }}
        >
          <TableVerticalSide {...getTableSideProps(this.state, this.props, { setExpanded })} />

          <div className="table-wrap">
            <ul {...getTableClassProperty(isPrint)}>
              <li className="thead">
                <ul>
                  {localPeriods.map((period, index) => {
                    return (
                      <li key={period.Text} id={`year_${period.Text}`}>
                        <span className="blue">
                          <Box display="flex" alignItems="center" justifyContent="flex-end">
                            {period.IsToggleable && !isPrint && (
                              <RawButton
                                css="margin-right: 4px; display: inline-block; line-height: 0"
                                onClick={() => {
                                  const expandCurrentYear = !period.IsMonth;

                                  this.fetch(is_current, fpId, clientId, expandCurrentYear);
                                }}
                                aria-label="View Current Year"
                              >
                                <Icon
                                  className={
                                    period.IsMonth ? 'far fa-minus-circle' : 'far fa-plus-circle'
                                  }
                                />
                              </RawButton>
                            )}
                            {period.Text}
                          </Box>
                        </span>
                        <span>{PrincipalAgeOptions[index]}</span>
                        {!!SpouseAgeOptions && <span>{SpouseAgeOptions[index]}</span>}
                      </li>
                    );
                  })}
                </ul>
              </li>

              <li className="tbody">
                <ul
                  {...getRowClassProperty(assetsSubheaderOffset, assetsSubheaderOffset, true, '')}
                >
                  {!!localPeriods.length &&
                    localPeriods.map((period, index) => {
                      const item = ProjectedItems[index];

                      return (
                        <li
                          key={period.Text}
                          className="has-breakdown"
                          onMouseEnter={e =>
                            this.show_breakdown(e, {
                              earnings: item.Assets.AssetsInvestmentEarnings,
                              contributions: item.Assets.AssetsContributions,
                              distributions: item.Assets.AssetsDistributions,
                            })
                          }
                          onMouseLeave={e => this.hide_breakdown(e)}
                        >
                          {`$${ADV.util.number_format(item.Assets.AssetsBalance, 0)}`}
                        </li>
                      );
                    })}
                </ul>

                {!!asset_groups.length &&
                  asset_groups.map((asset, gi) => {
                    const expanded = expanded_rows.includes(`Total|${asset.AssetGroup}`);
                    const ret_elements = [
                      <ul
                        {...getRowClassProperty(
                          gi + assetsGroupsOffset,
                          gi + assetsGroupsOffset,
                          false,
                          '',
                        )}
                        key={gi}
                      >
                        {!!localPeriods.length &&
                          localPeriods.map((period, index) => {
                            const asset_group = ProjectedItems[index].Assets.AssetGroups.find(
                              x => x.AssetGroup === asset.AssetGroup,
                            );

                            return (
                              <li
                                key={period.Text}
                                className="has-breakdown"
                                onMouseEnter={e => {
                                  this.show_breakdown(e, {
                                    contributions: asset_group.AssetGroupContributions,
                                    earnings: asset_group.AssetGroupInvestmentEarnings,
                                    distributions: asset_group.AssetGroupDistributions,
                                    investmentIncomeTax:
                                      asset_group.AssetGroupShortTermCapitalGainsTax,
                                    capitalGainsTax: asset_group.AssetGroupLongTermCapitalGainsTax,
                                    costBasis: asset_group.AssetGroupCostBasis,
                                    exerciseCost: asset_group.ExerciseCost,
                                    cashInterestTax: asset_group.CashInterestTax,
                                  });
                                }}
                                onMouseLeave={e => this.hide_breakdown(e)}
                              >
                                {asset_group ? (
                                  `$${ADV.util.number_format(asset_group.AssetGroupBalance, 0)}`
                                ) : (
                                  <span className="endash" />
                                )}
                              </li>
                            );
                          })}
                      </ul>,
                    ];

                    if (expanded && !!asset.AssetDetails.length) {
                      asset.AssetDetails.forEach((detail, i) => {
                        ret_elements.push(
                          <ul
                            {...getRowClassProperty(
                              gi + assetsGroupsOffset,
                              gi + i + 1 + assetsGroupsOffset,
                              false,
                              '',
                            )}
                          >
                            {!!localPeriods.length &&
                              localPeriods.map((period, index) => {
                                const asset_group = ProjectedItems[index].Assets.AssetGroups.find(
                                  x => x.AssetGroup === asset.AssetGroup,
                                );
                                const local_detail = asset_group.AssetDetails[i];
                                return (
                                  <li
                                    key={period.Text}
                                    className="has-breakdown"
                                    onMouseEnter={e => {
                                      if (detail) {
                                        this.show_breakdown(e, {
                                          earnings: local_detail.InvestmentEarnings,
                                          contributions: local_detail.Contributions,
                                          distributions: local_detail.Distributions,
                                          investmentIncomeTax: local_detail.InvestmentIncomeTax,
                                          capitalGainsTax: local_detail.CapitalGainsTax,
                                          costBasis: local_detail.CostBasis,
                                          exerciseCost: local_detail.ExerciseCost,
                                          cashInterestTax: local_detail.CashInterestTax,
                                        });
                                      }
                                    }}
                                    onMouseLeave={e => this.hide_breakdown(e)}
                                  >
                                    {`$${ADV.util.number_format(local_detail.Balance, 0)}`}
                                  </li>
                                );
                              })}
                          </ul>,
                        );
                      });
                    }
                    return ret_elements;
                  })}

                <ul
                  {...getRowClassProperty(
                    liabilitiesSubheaderOffset,
                    liabilitiesSubheaderOffset,
                    true,
                    '',
                  )}
                >
                  {!!localPeriods.length &&
                    localPeriods.map((period, index) => {
                      const item = ProjectedItems[index];

                      return (
                        <li
                          key={period.Text}
                          className="has-breakdown"
                          onMouseEnter={e =>
                            this.show_breakdown(e, {
                              payment: item.Liabilities.LiabilityPayment,
                              interest: item.Liabilities.LiabilityInterest,
                            })
                          }
                          onMouseLeave={e => this.hide_breakdown(e)}
                        >
                          {`$${ADV.util.number_format(item.Liabilities.LiabilitiesBalance, 0)}`}
                        </li>
                      );
                    })}
                </ul>

                {!!liability_groups.length &&
                  liability_groups.map((liability, gi) => {
                    const expanded = expanded_rows.includes(`Total|${liability.LiabilityGroup}`);
                    const ret_elements = [
                      <ul
                        {...getRowClassProperty(
                          gi + liabilitiesGroupsOffset,
                          gi + liabilitiesGroupsOffset,
                          false,
                          '',
                        )}
                        key={gi}
                      >
                        {!!localPeriods.length &&
                          localPeriods.map((period, index) => {
                            const liability_group = ProjectedItems[
                              index
                            ].Liabilities.LiabilityGroups.find(
                              x => x.LiabilityGroup === liability.LiabilityGroup,
                            );
                            return (
                              <li
                                key={period.Text}
                                className="has-breakdown"
                                onMouseEnter={e => {
                                  this.show_breakdown(e, {
                                    payment: liability_group.LiabilityGroupPayment,
                                    interest: liability_group.LiabilityGroupInterest,
                                  });
                                }}
                                onMouseLeave={e => this.hide_breakdown(e)}
                              >
                                {liability_group ? (
                                  `$${ADV.util.number_format(
                                    liability_group.LiabilityGroupBalance,
                                    0,
                                  )}`
                                ) : (
                                  <span className="endash" />
                                )}
                              </li>
                            );
                          })}
                      </ul>,
                    ];

                    if (expanded && !!liability.LiabilityDetails.length) {
                      liability.LiabilityDetails.forEach((detail, i) => {
                        ret_elements.push(
                          <ul
                            {...getRowClassProperty(
                              gi + liabilitiesGroupsOffset,
                              gi + i + 1 + liabilitiesGroupsOffset,
                              false,
                              '',
                            )}
                            key={i}
                          >
                            {!!localPeriods.length &&
                              localPeriods.map((period, index) => {
                                const liability_group = ProjectedItems[
                                  index
                                ].Liabilities.LiabilityGroups.find(
                                  x => x.LiabilityGroup === liability.LiabilityGroup,
                                );
                                const local_detail = liability_group.LiabilityDetails.find(
                                  x => x.Id === detail.Id,
                                );

                                return (
                                  <li
                                    key={period.Text}
                                    className="has-breakdown"
                                    onMouseEnter={e => {
                                      if (detail) {
                                        this.show_breakdown(e, {
                                          payment: local_detail.Payment,
                                          interest: local_detail.Interest,
                                          unmetEstateObligation: local_detail.UnmetEstateObligation,
                                        });
                                      }
                                    }}
                                    onMouseLeave={e => this.hide_breakdown(e)}
                                  >
                                    {`$${ADV.util.number_format(local_detail.Balance, 0)}`}
                                  </li>
                                );
                              })}
                          </ul>,
                        );
                      });
                    }

                    return ret_elements;
                  })}

                <ul {...getRowClassProperty(netWorthOffset, netWorthOffset, true, '', true)}>
                  {!!localPeriods.length &&
                    localPeriods.map((period, index) => {
                      const lessThanZero = ProjectedItems[index].Networth < 0;
                      return (
                        <li key={period.Text} className={`${lessThanZero ? 'red' : null}`}>
                          {`$${ADV.util.number_format(
                            Math.abs(ProjectedItems[index].Networth),
                            0,
                          )}`}
                        </li>
                      );
                    })}
                </ul>
              </li>
            </ul>
          </div>

          {show_breakdown && (
            <ProjectedBalanceSheetBreakdown
              breakdown={breakdown}
              style={{
                bottom: window.innerHeight - breakdown_top + 10,
                left: breakdown_left + breakdown_width / 2,
              }}
            />
          )}
        </section>
      );

      return (
        <div className={isPrint ? 'is-print ProjectedBalanceSheet' : 'ProjectedBalanceSheet'}>
          {isPrint
            ? [<PDFTitle title={title} />, widget]
            : [
                <header key={1}>
                  <Text as="h1" fontSize="h3" fontWeight={600} mb="16">
                    Balance Sheet
                  </Text>
                  <Box className="milestone-selector-container hide-on-mobile-screens">
                    <AdvForm>
                      <Select
                        className="md"
                        placeholder="Jump to Milestone"
                        options={Milestones.map(m => ({
                          label: m.Name,
                          value: m.Year,
                        }))}
                        onChange={this.scrollToYear}
                        aria-label="adv-jump-to-milestone"
                        css="width: 230px"
                      />
                    </AdvForm>
                    <Button
                      variant="subtleInactive"
                      leftIcon="far fa-file-export"
                      onClick={() => {
                        const expandCurrentYear =
                          Array.isArray(Periods) && Periods.some(p => p.IsMonth);
                        window.open(
                          urlWithParams('/Consumer/ProjectedBalanceSheet/DownloadCsv', {
                            clientId,
                            fpId,
                            isCurrent: is_current,
                            expandCurrentYear,
                          }),
                          '_blank',
                          'noopener noreferrer',
                        );
                      }}
                    >
                      Export as CSV
                    </Button>
                  </Box>
                </header>,
                <section className="widgetize" key={2}>
                  <button
                    type="button"
                    onClick={() => scroller.call(this, 'left')}
                    className={`scroll left${stopLeftScroll || isScrolling ? ' disabled' : ''}`}
                    aria-label="Scroll Left"
                  />
                  <button
                    type="button"
                    onClick={() => scroller.call(this, 'right')}
                    className={`scroll right${stopRightScroll || isScrolling ? ' disabled' : ''}`}
                    aria-label="Scroll Right"
                  />
                  {widget}
                </section>,
              ]}
        </div>
      );
    }
    return null;
  }
}

ProjectedBalanceSheetView.defaultProps = {
  ProjectedItems: null,
  Periods: null,
  PrincipalAgeOptions: [],
  SpouseAgeOptions: [],
  isPrint: false,
  error_type: '',
  errors: null,
  Milestones: [],
  is_current: false,
  is_projected: false,
  title: 'Balance Sheet',
};

ProjectedBalanceSheetView.propTypes = {
  is_initialized: PropTypes.bool.isRequired,
  ProjectedItems: ProjectedItemsPropTypes,
  Periods: PeriodsPropTypes,
  is_current: PropTypes.bool,
  is_projected: PropTypes.bool,
  PrincipalAgeOptions: PropTypes.arrayOf(PropTypes.string),
  SpouseAgeOptions: PropTypes.arrayOf(PropTypes.string),
  isPrint: PropTypes.bool,
  clientId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  fpId: PropTypes.string.isRequired,
  error_type: PropTypes.string,
  errors: PropTypes.arrayOf(PropTypes.string),
  Milestones: PropTypes.arrayOf(
    PropTypes.shape({
      Name: PropTypes.string,
      Year: PropTypes.number,
    }),
  ),
  fetch_data: PropTypes.func.isRequired,
  set_projected: PropTypes.func.isRequired,
  portalName: PropTypes.string.isRequired,
  title: PropTypes.string,
};

export default ProjectedBalanceSheetView;
